// pages/index.tsx
import React, { useCallback, useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Layout from '../components/Layout';
import Lvl3Landing from '../components/Lvl3Landing';
import { useFirebase } from '../lib/firebaseContext';

export default function Home(): JSX.Element {
  const router = useRouter();
  const { isAuthenticated, firebaseService, currentUser, authLoading, firebaseInitialized } = useFirebase();
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>('inactive');
  const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(true);

  const checkSubscriptionStatus = useCallback(async (): Promise<void> => {
    if (!firebaseService || !currentUser?.uid || !firebaseInitialized) {
      // setSubscriptionLoading(false);
      return;
    }

    try {
      setSubscriptionLoading(true);
      console.log('[index.tsx] 🔍 Checking subscription status for user:', currentUser.uid);

      // Get user document from Firestore
      const userDoc = await firebaseService.getUserDocument(currentUser.uid);

      if (userDoc && userDoc.subscriptionStatus) {
        const status = userDoc.subscriptionStatus;
        console.log('[index.tsx] 📋 User subscription status:', status);
        setSubscriptionStatus(status);

        // Redirect to pricing if subscription is not active
        if (status !== 'active') {
          console.log('[index.tsx] ❌ Subscription not active');
          // window.location.href = '/pricing';
          return;
        }

        console.log('[index.tsx] ✅ Subscription is active, proceeding to chat');
      } else {
        console.log('[index.tsx] ❌ No subscription status found');
        // window.location.href = '/pricing';
        return;
      }
    } catch (error) {
      console.error('[index.tsx] ❌ Error checking subscription status:', error);
      // On error, redirect to pricing for safety
      // window.location.href = '/pricing';
      return;
    } finally {
      setSubscriptionLoading(false);
    }
  }, [firebaseService, currentUser?.uid, firebaseInitialized]);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  // Show loading state only when actively checking auth (and not during SSR)
  if (authLoading && firebaseInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #030304 0%, #070709 100%)',
        color: 'white'
      }}>
        Loading...
      </div>
    );
  }

  // Handler for the "Chat Now" button
  const handleChatClick = (): void => {
    if (subscriptionLoading) {
      console.log('⏳ Still checking subscription status, please wait...');
      return;
    }

    if (isAuthenticated && subscriptionStatus === 'active' && currentUser) {
      console.log('✅ Subscription is active, proceeding to dashboard');
      router.push('/dashboard');
    } else if (!isAuthenticated && subscriptionStatus === 'inactive' && !currentUser) {
      router.push('/signup');
    } else if (isAuthenticated && subscriptionStatus !== 'active') {
      router.push('/pricing');
    } else {
      router.push('/signin');
    }
  };

  // Handler for the "Request Access" button
  const handleAccessClick = (): void => {
    if (isAuthenticated) {
      router.push('/conversations');
    } else {
      router.push('/pricing');
    }
  };

  return (
    <div>
      <Head>
        <title>LVL3.ai | Purpose Understood</title>
        <meta name="description" content="Experience AI that thinks faster than you can ask. Join the exclusive early access program for cutting-edge artificial intelligence." />
        <meta name="keywords" content="AI, artificial intelligence, machine learning, developer tools, API, advanced AI, LVL3" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Open Graph meta tags */}
        <meta property="og:title" content="LVL3.ai | Purpose Understood" />
        <meta property="og:description" content="Experience AI that thinks faster than you can ask. Join the exclusive early access program." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lvl3.ai" />

        {/* Twitter meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="LVL3.ai | Purpose Understood" />
        <meta name="twitter:description" content="Experience AI that thinks faster than you can ask." />

        {/* Performance optimizations */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="//api.lvl3.ai" />

        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      </Head>
      <Layout useCustomClass={true}>
        <Lvl3Landing
          onChatClick={handleChatClick}
          onAccessClick={handleAccessClick}
          className="w-full h-full"
        />
      </Layout>
      {/* Structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "LVL3.ai",
            "description": "Advanced AI platform for developers to build, deploy, and scale intelligent applications",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })
        }}
      />
    </div>
  );
}